import React, { useEffect, useRef } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import classnames from 'classnames'
import hasElementOrSomeParentTheClass from '@Utils/HasElementOrSomeParentTheClass'
import Overlay from '@SiteComponents/Overlay'
import AccountUserViewHeader from './AccountUserViewHeader'
import AccountUserWorkspacesTable from './AccountUserWorkspacesTable'
import Close from '@Images/languages_view_close.svg'
import Expand from '@Images/languages_view_expand.svg'
import Next from '@Images/languages_view_next.svg'
import Previous from '@Images/languages_view_previous.svg'

import {
  IAccountUser,
} from '@SiteModules/AccountUser/types'

import {
  getAccountUsers,
} from '@SiteModules/AccountUser/selectors'

import {
  deleteAccountUsers,
  changeRole,
} from '@SiteModules/AccountUser/actions'

import {
  getUserId,
} from '@SiteModules/User/selectors'

import './AccountUserView.scss'

interface AccountUserViewProps {
  readonly accountUser: IAccountUser | undefined
  readonly fullAccountUserView: boolean
  readonly shouldRender: boolean
  resetOpenedAccountUser(): void
  setOpenedAccountUserId(accountUserId: number): void
  setFullAccountUserView(newFullAccountUsersWorkspacesView: boolean): void
}

const AccountUserView: React.FC<AccountUserViewProps> = props => {
  const dispatch = useDispatch()

  const accountUsers = useSelector(getAccountUsers)
  const userId = useSelector(getUserId)

  const {
    fullAccountUserView,
    accountUser,
    shouldRender,
  } = props

  const halfAccountUserView = !fullAccountUserView && shouldRender
  const shouldRenderOverlay = halfAccountUserView

  const accountUsersRef = useRef(accountUsers)
  const indexOfOpenedAccountUser = accountUser ? accountUsers.indexOf(accountUser) : -1
  const indexOfOpenedAccountUserRef = useRef(indexOfOpenedAccountUser)
  const isFirstAccountUser = indexOfOpenedAccountUser === 0
  const isLastAccountUser = indexOfOpenedAccountUser === accountUsers.length - 1
  const hasWorkspaceUsers = accountUser && accountUser.workspaceUsers.length > 0

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (fullAccountUserView) { return }
      if (e.target) {
        const element = e.target as HTMLElement
        if (hasElementOrSomeParentTheClass(element, 'AccountUserView__element')) {
          return
        }
        props.resetOpenedAccountUser()
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [])

  useEffect(() => {
    indexOfOpenedAccountUserRef.current = indexOfOpenedAccountUser
  }, [indexOfOpenedAccountUser])

  useEffect(() => {
    accountUsersRef.current = accountUsers
  }, [accountUsers])

  const isArrowUp = (event: KeyboardEvent) => {
    return event.key === 'ArrowUp'
  }

  const isArrowDown = (event: KeyboardEvent) => {
    return event.key === 'ArrowDown'
  }

  const handleKeyDown = (event: KeyboardEvent) => {
    if (isArrowUp(event)) {
      event.preventDefault()
      setPreviousAccountUser()
    }

    if (isArrowDown(event)) {
      event.preventDefault()
      setNextAccountUser()
    }
  }

  const setNextAccountUser = () => {
    if (indexOfOpenedAccountUserRef.current < accountUsersRef.current.length - 1 && indexOfOpenedAccountUserRef.current > -1) {
      props.setOpenedAccountUserId(accountUsersRef.current[indexOfOpenedAccountUserRef.current + 1].id)
    }
  }

  const setPreviousAccountUser = () => {
    if (indexOfOpenedAccountUserRef.current > 0) {
      props.setOpenedAccountUserId(accountUsersRef.current[indexOfOpenedAccountUserRef.current - 1].id)
    }
  }

  const handleDeleteAccountUser = () => {
    if (!accountUser) { return }

    dispatch(deleteAccountUsers({ accountUserIds: [accountUser.id] }))
    props.resetOpenedAccountUser()
  }

  const handleChangeAccountRole = (role: string) => {
    if (!accountUser) { return }

    dispatch(changeRole({ accountUserId: accountUser.id, role }))
  }

  const renderActionsHalfView = () => {
    return (
      <div className="AccountUserView__actions">
        <Close
          className="AccountUserView__actions-close"
          onClick={props.resetOpenedAccountUser}
        />
        <Expand
          className="AccountUserView__actions-expand"
          onClick={props.setFullAccountUserView}
        />
        <div className="AccountUserView__actions-separator"/>
        <Next
          className={classnames(
            'AccountUserView__actions-next',
            { 'AccountUserView__actions-next-disabled': isLastAccountUser }
          )}
          onClick={setNextAccountUser}
        />
          <Previous
            className={classnames(
              'AccountUserView__actions-next',
              { 'AccountUserView__actions-previous-disabled': isFirstAccountUser }
            )}
            onClick={setPreviousAccountUser}
          />
      </div>
    )
  }

  const renderHeader = () => {
    return (
      <AccountUserViewHeader
        isOwnUser={!!accountUser && accountUser.attributes.userId === userId}
        accountUser={accountUser}
        fullAccountUserView={fullAccountUserView}
        resetOpenedAccountUser={props.resetOpenedAccountUser}
        deleteAccountUser={handleDeleteAccountUser}
        changeAccountRole={handleChangeAccountRole}
      />
    )
  }

  const renderAccountUserWorkspacesTable = () => {
    return (
      <AccountUserWorkspacesTable
        accountUser={accountUser as IAccountUser}
        fullAccountUserView={fullAccountUserView}
      />
    )
  }

  return (
    <React.Fragment>
      {shouldRenderOverlay && (
        <Overlay />
      )}
      <div className={classnames(
        'AccountUserView',
        'AccountUserView__element',
        { 'AccountUserView__hidden': !shouldRender },
        { 'AccountUserView__full': fullAccountUserView }
      )}>
        {halfAccountUserView && renderActionsHalfView()}
        {renderHeader()}
        {hasWorkspaceUsers && renderAccountUserWorkspacesTable()}
      </div>
    </React.Fragment>
  )
}

export default AccountUserView
